import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import type { PurgePreview } from '../types'

/**
 * Effacer ce qu'on choisit d'effacer.
 *
 * Une édition finit par peser : des milliers de commandes, des photos de
 * candidats, des factures scannées. Un an plus tard, garder tout cela n'a plus
 * d'intérêt — et garder des données personnelles sans raison n'est même pas
 * permis. Mais « tout supprimer » n'est pas une réponse : on veut jeter les
 * médias sans perdre les comptes, ou effacer des coordonnées sans effacer les
 * totaux.
 *
 * D'où un choix par domaine, et trois protections, parce qu'une suppression ne
 * se rattrape pas :
 *
 * * **on annonce avant de détruire** — chaque ligne porte le nombre exact ;
 * * **on retape le nom de l'édition** — une case se clique par accident,
 *   recopier un nom ne s'improvise pas ;
 * * **rien n'est coché d'avance**, et le bouton reste éteint tant qu'aucun
 *   domaine n'est choisi.
 *
 * Un domaine vide s'affiche quand même, désactivé : le faire disparaître
 * laisserait croire qu'on ne peut pas l'effacer.
 */
export function PurgePanel({ eventId }: { eventId: string | undefined }) {
  const { t } = useTranslation()
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState<Record<string, number> | null>(null)

  const preview = useAdminQuery<PurgePreview>(
    ['admin-purge-preview', eventId],
    `/admin/events/${eventId}/purge/preview`,
    eventId,
  )

  const purge = useAdminMutation<
    { destroyed: Record<string, number> },
    { scopes: string[]; confirm: string }
  >('post', () => `/admin/events/${eventId}/purge`, eventId, [
    ['admin-purge-preview', eventId ?? ''],
  ])

  if (preview.isPending) return <Skeleton className="h-56 w-full" />
  // Un compte sans le droit `event.event.purge` ne doit pas voir ce panneau :
  // le refus du serveur suffit à le masquer, sans écran d'erreur alarmant.
  if (preview.isError) return null

  const report = preview.data
  if (!report) return null

  const names = Object.keys(report.counts)
  const nothingChosen = chosen.size === 0
  const nameMatches =
    confirm.trim().toLowerCase() === report.event_name.trim().toLowerCase()

  function toggle(code: string) {
    setChosen((current) => {
      const next = new Set(current)
      if (!next.delete(code)) next.add(code)
      return next
    })
  }

  return (
    <Card className="mt-8 border-state-danger/30">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.purge.title')}
      </h2>
      <p className="mt-1 max-w-2xl text-sm text-content-secondary">
        {t('admin.purge.intro')}
      </p>

      <ul className="mt-5 grid gap-1.5 sm:grid-cols-2">
        {names.map((code) => {
          const count = report.counts[code] ?? 0
          const empty = count === 0
          return (
            <li key={code}>
              <label
                className={cn(
                  'flex items-center gap-3 rounded-md border px-3 py-2.5',
                  empty
                    ? 'border-line-subtle opacity-50'
                    : chosen.has(code)
                      ? 'border-state-danger/50 bg-state-danger/5'
                      : 'border-line-subtle',
                )}
              >
                <input
                  type="checkbox"
                  disabled={empty}
                  checked={chosen.has(code)}
                  onChange={() => toggle(code)}
                  className="h-4 w-4 shrink-0 accent-[var(--ember-500)]"
                />
                <span className="min-w-0 flex-1 text-sm text-content-primary">
                  {t(`admin.purge.scopes.${code}`)}
                </span>
                <span className="shrink-0 text-caption tabular-nums text-content-muted">
                  {t('admin.purge.rows', { count })}
                </span>
              </label>
            </li>
          )
        })}
      </ul>

      <div className="mt-5 max-w-md">
        <label className="block text-caption uppercase tracking-wide text-content-muted">
          {t('admin.purge.confirmLabel', { name: report.event_name })}
        </label>
        <input
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          placeholder={report.event_name}
          className="mt-1.5 w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
        />
      </div>

      {purge.error ? (
        <p className="mt-3 text-sm text-state-danger">{purge.error.message}</p>
      ) : null}

      {done ? (
        <p className="mt-3 text-sm text-state-success">
          {t('admin.purge.done', {
            count: Object.values(done).reduce((total, value) => total + value, 0),
          })}
        </p>
      ) : null}

      <Button
        variant="ember"
        className="mt-4"
        disabled={nothingChosen || !nameMatches || purge.isPending}
        onClick={() =>
          purge.mutate(
            { scopes: [...chosen], confirm },
            {
              onSuccess: (result) => {
                setDone(result.destroyed)
                setChosen(new Set())
                setConfirm('')
              },
            },
          )
        }
      >
        {t('admin.purge.action')}
      </Button>

      <p className="mt-3 text-xs text-content-muted">{t('admin.purge.kept')}</p>
    </Card>
  )
}
